import { type FC, useEffect, useState } from 'react';
import {
  CalendarOutlined,
  CloseCircleOutlined,
  FileExcelOutlined,
  IssuesCloseOutlined,
  MobileOutlined,
  QuestionCircleOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { isEmpty } from 'lodash';
import { Form, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { Modal, Radio, Space, Switchbox, Text, Textarea } from '@g2u/ui-kit';

import { type JsonDataItem, type WithOptionalDataTest, AccountType, PricingCategory } from 'types';
import { getCheckLostJobs, getLostJobReasons, postLostJob } from 'queries';
import { Toast, parseErrors, validateFormSilently } from 'helpers';
import { InputEmail, InputName } from 'components';
import { useText } from 'helpers/hooks/useText';
import { BookingStore } from 'store';
import styles from './lost-job-modal.module.scss';

type Props = {
  isOpen: boolean;
  onOk: () => void;
} & WithOptionalDataTest;

const iconMap: JsonDataItem = {
  'fa-question': <QuestionCircleOutlined />,
  'fa-user-times': <UserDeleteOutlined />,
  'fa-user': <FileExcelOutlined />,
  'fa-mobile': <MobileOutlined />,
  'fa-globe': <IssuesCloseOutlined />,
  'fa-calendar-times-o': <CalendarOutlined />,
  'fa-ban': <CloseCircleOutlined />,
};

export const LostJobModal: FC<Props> = observer((props) => {
  const { isOpen, onOk, dataTestId } = props;
  const { lostJob, setLostJob, setLostJobReasons } = BookingStore;
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [showEmailForm, setShowEmailForm] = useState<boolean>(false);
  const { getText } = useText();
  const [form] = Form.useForm();
  Form.useWatch('reason', { form });

  const { mutate: checkLostJob } = useMutation(getCheckLostJobs, {
    onSuccess: (data) => {
      setLostJob(data);
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const {
    mutate: checkReasons,
    data: lostJobOptions,
    isLoading: isReasonsLoading,
  } = useMutation(getLostJobReasons, {
    onSuccess: (data) => {
      setLostJobReasons(data);
      validateFormSilently(form, setIsFormValid);
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: reportLostJob } = useMutation(postLostJob, {
    onSuccess: (data) => {
      Toast.show({
        description: `Lost Job #${data.lostJobID} has been reported. `,
        type: Toast.Type.Success,
      });
      onOk();
      setLostJobReasons(null);
      checkLostJob();
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  // check account or form selection (api expects 'B' instead of 'C' if company)
  const isCompanyAccount = lostJobOptions?.customer?.type === 'Company';
  const isPersonalAccount = lostJobOptions?.customer?.type === 'Personal';
  const customerType = isCompanyAccount ? PricingCategory.Business : isPersonalAccount ? AccountType.Personal : 'U';

  const handleFormSubmit = (formData: JsonDataItem | null) => {
    reportLostJob({
      searchId: lostJob?.searchHistoryID,
      reason: formData?.reason,
      customerType: formData?.customerType,
      sendEmail: Number(formData?.sendEmail),
      name: formData?.name,
      email: formData?.email,
      customerId: lostJobOptions?.customer?.ID,
      extraInfo: formData?.info,
    });
    setShowEmailForm(false);
  };

  const handleFormChange = (changedField: JsonDataItem) => {
    Object.keys(changedField).includes('sendEmail') && setShowEmailForm(changedField.sendEmail);
    void (!isEmpty(form.getFieldsValue()) && validateFormSilently(form, setIsFormValid));
  };

  useEffect(() => {
    void (Object.keys(form.getFieldsValue()).length && validateFormSilently(form, setIsFormValid));
  }, [showEmailForm]);

  useEffect(() => {
    isOpen && lostJob?.searchHistoryID && checkReasons({ id: lostJob?.searchHistoryID });
  }, [isOpen]);

  return (
    <Modal.$
      open={isOpen}
      destroyOnClose
      maskClosable={false}
      title={'Record Lost Job'}
      okText="Save"
      onOk={() => {
        form.submit();
      }}
      okButtonProps={{
        loading: false,
        disabled: !isFormValid,
      }}
      closable={false}
      cancelButtonProps={{ style: { display: 'none' } }}
      dataTestId={dataTestId ?? 'lost-job-modal'}
    >
      <Form
        form={form}
        name="lost-job-form"
        layout="vertical"
        onValuesChange={handleFormChange}
        onFinish={handleFormSubmit}
        colon={false}
        preserve={false}
      >
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraLarge} style={{ marginBottom: '-16px' }}>
          {isReasonsLoading ? (
            <Space.$ justify={Space.Justify.Center}>
              <Spin />
            </Space.$>
          ) : (
            <>
              <div>
                <Space.$
                  direction={Space.Direction.Vertical}
                  compact
                  className={`${styles.lostJobModal__script} script-box`}
                >
                  <Text.$>{getText('booking.lostJob.acknowledgment')}</Text.$>
                  <Text.$>{getText('booking.lostJob.sendInformation')}</Text.$>
                  <Text.$>{getText('booking.lostJob.noEmailOption')}</Text.$>
                  <Text.$ type="secondary" italic>
                    {getText('booking.lostJob.waitBeforeHangup')}
                  </Text.$>
                </Space.$>
                <Radio.$
                  name="reason"
                  required
                  label="Please select one of the reasons:"
                  initialValue={
                    lostJobOptions?.reasons.length === 1 ? lostJobOptions.reasons[0].ID.toString() : undefined
                  }
                  buttons
                  columns={2}
                  options={lostJobOptions?.reasons
                    .sort((a: JsonDataItem, b: JsonDataItem) => a.reason_order - b.reason_order)
                    .map((option: JsonDataItem) => {
                      return {
                        label: (
                          <>
                            {iconMap[option.icon]} {option.reason}
                          </>
                        ),
                        value: option.ID.toString(),
                      };
                    })}
                  rules={[{ required: true }]}
                />
              </div>
              {lostJobOptions?.reasons.some(
                (reason: JsonDataItem) =>
                  reason.show_additional_info === 1 && reason.ID.toString() === form.getFieldValue('reason'),
              ) && (
                <Textarea.$
                  name="info"
                  placeholder=""
                  required
                  rules={[{ required: true }]}
                  label={<Text.$>Please provide the extra info:</Text.$>}
                />
              )}
              <Radio.$
                name="customerType"
                required
                label="Please select the customer type:"
                initialValue={customerType}
                buttons
                columns={3}
                options={[
                  {
                    label: 'Home Customer',
                    value: AccountType.Personal,
                  },
                  {
                    label: 'Business Customer',
                    value: PricingCategory.Business,
                  },
                  {
                    label: 'Unknown',
                    value: 'U',
                  },
                ]}
                rules={[{ required: true }]}
              />
              <Space.$ size={Space.Size.Middle} direction={Space.Direction.Vertical}>
                <Switchbox.$ name="sendEmail" label="Send pricing email" initialValue={showEmailForm} horizontal />
                {showEmailForm && (
                  <>
                    <InputName
                      name="name"
                      required
                      label="Customer Name :"
                      placeholder="First Name"
                      initialValue={lostJobOptions?.customer?.contact1_firstname}
                      className={styles.lostJobModal__row}
                    />
                    <InputEmail
                      name="email"
                      required
                      disableEmailCheck
                      label="Customer Email :"
                      placeholder="Email Address"
                      initialValue={lostJobOptions?.customer?.email}
                      className={styles.lostJobModal__row}
                    />
                  </>
                )}
              </Space.$>
            </>
          )}
        </Space.$>
      </Form>
    </Modal.$>
  );
});
